<template>
    <vueper-slides
        :class="{ 'no-shadow': !shadow, 'small-navigation': smallNavigation }"
        v-if="slides"
        :autoplay="isAutoplay"
        :visible-slides="visibleSlides"
        :slide-ratio="slideRatio"
        :gap="gap"
        :fixed-height="fixedHeight"
        :bullets="bullets"
    >
        <vueper-slide
            v-for="(slide, i) in slides"
            :key="i"
            :image="slide[imageKey]"
            :title="slide.title"
            :content="slide.content"
            :class="slideClass"
            :style="slideStyle"
        >
            <template
                #content
                v-if="$scopedSlots.content"
                v-slot:content
            >
                <slot
                    name="content"
                    :index="i"
                    :slide="slide"
                />
            </template>
        </vueper-slide>
    </vueper-slides>
</template>

<script>
import { VueperSlides, VueperSlide } from "vueperslides";
import "vueperslides/dist/vueperslides.css";

export default {
    name: "MaterialSlideShow",
    data() {
        return {};
    },

    components: { VueperSlides, VueperSlide },

    mounted() {
        if (this.autoHeight) {
            this.adjustSlideHeight();
            window.addEventListener("resize", this.adjustSlideHeight);
        }
    },
    beforeDestroy() {
        if (this.autoHeight) window.removeEventListener("resize", this.adjustSlideHeight);
    },
    computed: {
        isAutoplay() {
            return this.autoplay && this.slides && this.slides.length > 0;
        }
    },
    methods: {
        adjustSlideHeight() {
            this.$nextTick(() => {
                const el = this.$el;
                let h = 0;

                const slides = el.querySelectorAll(".vueperslide > *");
                slides.forEach(s => {
                    if (s.clientHeight > h) h = s.clientHeight;
                });

                if (!el.id) el.id = "_vps-" + this._uid;

                const wrapper = el.querySelector(".vueperslides__parallax-wrapper");
                if (wrapper) {
                    wrapper.style.height = h + "px";
                }
            });
        }
    },
    props: {
        slides: {
            type: Array,
            default: () => []
        },
        imageKey: {
            type: String,
            default: "image"
        },

        shadow: {
            type: Boolean,
            default: () => false
        },
        smallNavigation: {
            type: Boolean,
            default: () => false
        },
        autoHeight: {
            type: Boolean,
            default: false
        },
        slideClass: {
            type: String,
            default: ""
        },
        slideStyle: {
            type: String,
            default: ""
        },

        alwaysRefreshClones: { type: Boolean, default: false },
        arrows: { type: Boolean, default: true },
        arrowsOutside: { type: Boolean, default: null },
        autoplay: { type: Boolean, default: false },
        breakpoints: { type: Object, default: () => ({}) },
        bullets: { type: Boolean, default: true },
        bulletsOutside: { type: Boolean, default: null },
        disable: { type: Boolean, default: false },
        // Ability to disable arrows on slideshow edges. Only if not infinite mode.
        disableArrowsOnEdges: { type: [Boolean, String], default: false },
        // By default when touch is enabled you have to drag from a slide side and pass 50% of
        // slideshow width to change slide. This setting changes this behavior to a horizontal
        // pixel amount from anywhere on slideshow.
        draggingDistance: { type: Number, default: null },
        duration: { type: [Number, String], default: 4000 }, // Autoplay slide duration.
        infinite: { type: Boolean, default: true },
        fade: { type: Boolean, default: false },
        fixedHeight: { type: [Boolean, String], default: false },
        fractions: { type: Boolean, default: false },
        gap: { type: Number, default: 0 },
        initSlide: { type: Number, default: 1 },
        lazy: { type: Boolean, default: false },
        lazyLoadOnDrag: { type: Boolean, default: false },
        pauseOnHover: { type: Boolean, default: true },
        pauseOnTouch: { type: Boolean, default: true },
        parallax: { type: [Boolean, Number], default: false },
        pageScrollingElement: { type: String, default: "" },
        parallaxFixedContent: { type: Boolean, default: false },
        // This one is not modifiable through breakpoints: event handlers are added/removed.
        preventYScroll: { type: Boolean, default: false },
        progress: { type: Boolean, default: false },
        rtl: { type: Boolean, default: false },
        slideContentOutside: { type: [Boolean, String], default: false },
        slideContentOutsideClass: { type: String, default: "" },
        slideImageInside: { type: Boolean, default: false },
        slideMultiple: { type: [Boolean, Number], default: false },
        slideRatio: { type: Number, default: 1 / 3 },
        touchable: { type: Boolean, default: true },
        transitionSpeed: { type: [Number, String], default: 600 },
        visibleSlides: { type: Number, default: 1 },
        "3d": { type: Boolean, default: false }
    }
};
</script>

<style lang="scss">
.small-navigation {
    .vueperslides__arrows button {
        svg {
            width: 2.8em;
            stroke-width: 1.5;
        }
    }
}
</style>
